/**
 * Pre-folded search documents.
 *
 * Folding and tokenizing 1500+ recipes on every keystroke would be wasteful, so
 * the whole catalog is converted once per load and the scorer reads only from
 * these documents. Build this behind a `useMemo` keyed on the recipes array.
 */

import { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import { foldText, tokenize, stemToken } from '../utils/searchNormalize';

export interface SearchDoc {
  recipe: PersonalRecipeIndexEntry;
  /** Folded full title — used for whole-phrase matches ("bol poke"). */
  foldedTitle: string;
  titleTokens: string[];
  titleStems: string[];
  ingredientTokens: string[];
  ingredientStems: string[];
  /**
   * Dish type and cuisine tokens.
   *
   * Only ~40% of the catalog carries `dishTypes` and `cuisines` is empty on
   * every recipe today, so these can boost a score but must never gate one —
   * filtering on them would silently hide the majority of the catalog.
   */
  categoryTokens: string[];
  /** Deduped union of the above, for the fuzzy tier. */
  fuzzyTokens: string[];
}

function uniq(tokens: string[]): string[] {
  return Array.from(new Set(tokens));
}

/**
 * Convert the catalog into search documents. One pass, ~15ms for 1500 recipes.
 *
 * `translatedTitles` are folded in alongside the title so a French query can
 * reach an English-sourced recipe and vice versa.
 */
export function buildSearchDocs(recipes: PersonalRecipeIndexEntry[]): SearchDoc[] {
  return recipes.map((recipe) => {
    const titleSources = [
      recipe.title,
      recipe.translatedTitles?.fr,
      recipe.translatedTitles?.en,
    ].filter((t): t is string => Boolean(t));

    const titleTokens = uniq(titleSources.flatMap(tokenize));
    const ingredientTokens = uniq((recipe.ingredients ?? []).flatMap(tokenize));
    const categoryTokens = uniq(
      [...(recipe.dishTypes ?? []), ...(recipe.cuisines ?? [])].flatMap(tokenize)
    );

    return {
      recipe,
      foldedTitle: foldText(recipe.title),
      titleTokens,
      titleStems: uniq(titleTokens.map(stemToken)),
      ingredientTokens,
      ingredientStems: uniq(ingredientTokens.map(stemToken)),
      categoryTokens,
      fuzzyTokens: uniq([...titleTokens, ...ingredientTokens, ...categoryTokens]),
    };
  });
}
